import { fromEntries } from '@direct-frontend/stdlib';
import type { OpenApiLinkDeliveryServiceType } from '#shared/api/schema';
import { DELIVERY_META_SHIP_SERVICES, DELIVERY_SERVICES } from '#shared/lib/constants';
import { type Replace } from '#shared/lib/types';

const META_SHIP_PREFIX = 'META_SHIP_';

/*
 * Преобразует название сервиса в путь на уровне типов.
 * - META_SHIP_DALLI -> meta_ship/dalli
 * - YANDEX_DELIVERY -> yandex_delivery
 */
type ServicePath<S extends string> = S extends `META_SHIP_${infer Suffix}`
    ? `meta_ship/${Lowercase<Suffix>}`
    : Lowercase<S>;

/*
 * Преобразует название сервиса в путь.
 * - Яндекс/СДЭК: YANDEX_DELIVERY -> yandex_delivery, CDEK -> cdek
 * - MetaShip: META_SHIP_DALLI -> meta_ship/dalli
 */
const getServicePath = (service: OpenApiLinkDeliveryServiceType): string => {
    if (DELIVERY_META_SHIP_SERVICES.includes(service)) {
        const suffix = service.replace(META_SHIP_PREFIX, '').toLowerCase();

        return `meta_ship/${suffix}`;
    }

    return service.toLowerCase();
};

/*
 *  Функция строит пути для настроек сервисов доставки и возвращает объект,
 *  ключами которого являются константы для связи с путями до настроек,
 *  значениями являются сами пути до настроек
 *
 *       getDeliveryServiceSlug('delivery', 'shipping_schedule') ->
 *      {
 *          YANDEX_DELIVERY_SHIPPING_SCHEDULE: "delivery/yandex_delivery/shipping_schedule",
 *          CDEK_SHIPPING_SCHEDULE: "delivery/cdek/shipping_schedule",
 *          META_SHIP_DALLI_SHIPPING_SCHEDULE: "delivery/meta_ship/dalli/shipping_schedule"
 *      }
 *
 *      getDeliveryServiceSlug('delivery', 'free_delivery_from/enabled') ->
 *      {
 *          YANDEX_DELIVERY_FREE_DELIVERY_FROM_ENABLED: "delivery/yandex_delivery/free_delivery_from/enabled",
 *          CDEK_FREE_DELIVERY_FROM_ENABLED: "delivery/cdek/free_delivery_from/enabled",
 *          META_SHIP_DALLI_FREE_DELIVERY_FROM_ENABLED: "delivery/meta_ship/dalli/free_delivery_from/enabled"
 *      }
 */
export const getDeliveryServiceSlug = <Branch extends string, Slug extends string>(
    branch: Branch,
    slug: Slug,
    services: Array<OpenApiLinkDeliveryServiceType> = DELIVERY_SERVICES
) =>
    fromEntries(
        services.map((service) => [
            `${service}_${slug.toUpperCase().replace('/', '_')}`,
            `${branch}/${getServicePath(service)}/${slug}`,
        ])
    ) as {
        [Key in (typeof services)[number] as `${Key}_${Uppercase<Replace<Slug, '/', '_'>>}`]: `${Branch}/${ServicePath<Key>}/${Slug}`;
    };
